/**
 * 목차 + 배열 → 채널 — 기록된 시각을 episode 공통 원점 기준 초로 옮긴다.
 *
 * **원점은 하나다.** 채널마다 첫 시각을 빼서 각각 0 으로 만들면 원래 지연이 지워진다(명세 §6).
 * ns 는 정수 원점을 먼저 뺀 뒤 초로 바꾼다 — float64 로 epoch 를 먼저 만들면 1ns 간격을 잃는다.
 *
 * Owns:
 * - 배열 종류 → 채널 값, 원점 해석, 길이가 어긋난 배열의 거부.
 * - 두 채널을 한 pose 로 합쳐도 되는지의 판정(`alignmentProblem`). BE 매핑 계산과 같은 규칙이다.
 *
 * Does not own:
 * - 바이트 받기 · 디코드 — `api/inspectApi.ts` · `@/shared/parquet`.
 */
import type { ParquetColumn } from "@/shared/parquet";
import type {
  EpisodeInspect,
  InspectChannel,
  InspectStream,
  InspectTimeOrigin,
} from "./episodeInspect";
import { isVectorStream } from "./episodeInspect";

const NANOS_PER_SECOND = 1_000_000_000n;

export class InspectChannelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InspectChannelError";
  }
}

/** 배열 파일 경로 → 디코드된 컬럼. */
export type InspectArrays = ReadonlyMap<string, ParquetColumn>;

/**
 * 재생 클럭이 설 원점.
 *
 * `confirmed` 가 거짓이면 서버가 고른 채널이 없다는 뜻이다 — 가장 이른 첫 시각을 표시용
 * 원점으로 쓰되 화면은 "공통 시간축 미확인" 으로 알린다. 채널 사이의 간격은 그대로 남는다.
 */
export type ResolvedOrigin = {
  confirmed: boolean;
  channel: string | null;
  seconds: number;
  nanos: bigint;
};

/** 목차가 선언한 원점을 배열에서 읽은 값과 맞춘다. */
export function resolveOrigin(
  origin: InspectTimeOrigin,
  streams: readonly InspectStream[],
  arrays: InspectArrays,
): ResolvedOrigin {
  if (origin.value !== null && origin.unit !== null) {
    return origin.unit === "ns"
      ? { confirmed: true, channel: origin.channel, seconds: 0, nanos: BigInt(origin.value) }
      : { confirmed: true, channel: origin.channel, seconds: Number(origin.value), nanos: 0n };
  }
  // 서버가 원점을 고르지 않았다 — 가장 이른 첫 시각 하나를 표시용으로 쓴다(채널별 0 이 아니다).
  let seconds = Number.POSITIVE_INFINITY;
  let nanos: bigint | null = null;
  for (const stream of streams) {
    const column = arrays.get(stream.timestamps.path);
    if (column === undefined) continue;
    if (column.kind === "bigint" && column.rows.length > 0) {
      const first = column.rows[0];
      nanos = nanos === null || first < nanos ? first : nanos;
    }
    if (column.kind === "number" && column.rows.length > 0) {
      seconds = Math.min(seconds, column.rows[0]);
    }
  }
  return {
    confirmed: false,
    channel: null,
    seconds: Number.isFinite(seconds) ? seconds : 0,
    nanos: nanos ?? 0n,
  };
}

/**
 * 채널 하나를 시간축과 값으로 푼다.
 *
 * 배열이 빠졌거나 길이가 시각과 다르면 값을 지어내지 않고 멈춘다 — 화면이 안내한다.
 */
export function buildChannel(
  stream: InspectStream,
  arrays: InspectArrays,
  origin: ResolvedOrigin,
): InspectChannel {
  const timestamps = arrayFor(arrays, stream.timestamps.path, stream.key, "timestamps");
  const secondsFromOrigin = toSeconds(timestamps, origin, stream.key);
  const validity = toValidity(
    arrayFor(arrays, stream.validity.path, stream.key, "validity"),
    secondsFromOrigin.length,
    stream.key,
  );
  if (isVectorStream(stream)) {
    return {
      key: stream.key,
      clockId: stream.clock.id,
      secondsFromOrigin,
      validity,
      sourceRows:
        stream.sourceRows === null
          ? null
          : toRowNumbers(
              arrayFor(arrays, stream.sourceRows.path, stream.key, "source_rows"),
              stream.key,
            ),
      values: toValueRows(
        arrayFor(arrays, stream.values.path, stream.key, "values"),
        secondsFromOrigin.length,
        stream.key,
      ),
      presentationSeconds: null,
    };
  }
  return {
    key: stream.key,
    clockId: stream.clock.id,
    secondsFromOrigin,
    validity,
    sourceRows: null,
    values: null,
    presentationSeconds:
      stream.presentationSeconds === null
        ? null
        : toPlainSeconds(
            arrayFor(arrays, stream.presentationSeconds.path, stream.key, "presentation_seconds"),
            stream.key,
          ),
  };
}

/** 받은 배열로 만들 수 있는 채널 전부. 배열이 아직 없는 채널은 빠진다. */
export function buildChannels(inspect: EpisodeInspect, arrays: InspectArrays): InspectChannel[] {
  const origin = resolveOrigin(inspect.timeOrigin, inspect.streams, arrays);
  return inspect.streams
    .filter((stream) => arrays.has(stream.timestamps.path))
    .map((stream) => buildChannel(stream, arrays, origin));
}

/**
 * 두 채널이 같은 표본이 아닌 사유. BE `compose.py` 의 `pose.clock_mismatch` ·
 * `pose.row_count_mismatch` · `pose.timestamp_mismatch` 와 짝이다. BE 는 시계 ID 가 없는 채널도
 * `pose.clock_mismatch` 로 거부한다. FE 는 그 경우를 `clock_unknown` 으로 따로 말한다.
 */
export type ChannelAlignmentReason =
  "clock_unknown" | "clock_mismatch" | "row_count_mismatch" | "timestamp_mismatch";

/**
 * 위치와 자세를 서로 다른 채널에서 읽을 때, 두 채널을 한 pose 로 합쳐도 되는지. 아니면 사유다.
 *
 * 같은 채널이면 볼 것이 없다. 다른 채널이면 시계가 같고, 행 수가 같고, 행마다 시각이 같아야 한다.
 * 시각은 허용 오차 없이 본다. 같은 시계의 같은 시각은 같은 원점을 같은 식으로 뺀 값이라 같다. 보간 ·
 * 최근접 표본 선택으로 어긋남을 지우지 않는다(BE 재설계 §6).
 */
export function alignmentProblem(
  a: InspectChannel,
  b: InspectChannel,
): ChannelAlignmentReason | null {
  if (a.key === b.key) return null;
  if (a.clockId === null || b.clockId === null) return "clock_unknown";
  if (a.clockId !== b.clockId) return "clock_mismatch";
  if (a.secondsFromOrigin.length !== b.secondsFromOrigin.length) return "row_count_mismatch";
  return a.secondsFromOrigin.every((second, row) => second === b.secondsFromOrigin[row])
    ? null
    : "timestamp_mismatch";
}

function arrayFor(arrays: InspectArrays, path: string, key: string, field: string): ParquetColumn {
  const column = arrays.get(path);
  if (column === undefined) {
    throw new InspectChannelError(`channel ${key}: ${field} array is missing`);
  }
  return column;
}

function toSeconds(column: ParquetColumn, origin: ResolvedOrigin, key: string): number[] {
  if (column.kind === "bigint") {
    // 정수 원점을 먼저 뺀다 — epoch 를 float64 로 만들면 1ns 간격이 사라진다.
    return column.rows.map((value) => Number(value - origin.nanos) / Number(NANOS_PER_SECOND));
  }
  if (column.kind === "number") {
    return column.rows.map((value) => value - origin.seconds);
  }
  throw new InspectChannelError(`channel ${key}: timestamps are not numeric`);
}

function toPlainSeconds(column: ParquetColumn, key: string): number[] {
  if (column.kind === "number") return [...column.rows];
  if (column.kind === "bigint") {
    return column.rows.map((value) => Number(value) / Number(NANOS_PER_SECOND));
  }
  throw new InspectChannelError(`channel ${key}: presentation seconds are not numeric`);
}

function toValidity(column: ParquetColumn, rows: number, key: string): boolean[] {
  if (column.kind !== "boolean") {
    throw new InspectChannelError(`channel ${key}: validity is not boolean`);
  }
  if (column.rows.length !== rows) {
    throw new InspectChannelError(`channel ${key}: validity length differs from timestamps`);
  }
  return [...column.rows];
}

function toRowNumbers(column: ParquetColumn, key: string): number[] {
  if (column.kind === "bigint") return column.rows.map(Number);
  if (column.kind === "number") return [...column.rows];
  throw new InspectChannelError(`channel ${key}: source rows are not numeric`);
}

/** scalar 컬럼도 길이 1 행으로 펴 소비자가 한 모양만 본다. */
function toValueRows(column: ParquetColumn, rows: number, key: string): number[][] {
  const values =
    column.kind === "numberList"
      ? column.rows.map((row) => [...row])
      : column.kind === "number"
        ? column.rows.map((value) => [value])
        : null;
  if (values === null) {
    throw new InspectChannelError(`channel ${key}: values are not numeric`);
  }
  if (values.length !== rows) {
    throw new InspectChannelError(`channel ${key}: values length differs from timestamps`);
  }
  return values;
}
